import React, { useEffect, useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import { apiUrl } from "../../config";

const ProductImageForm = ({ initialValues, onSubmit }) => {
  const [formState, setFormState] = useState({
    productId: "",
    type: "",
    imageUrl: null, // can be File or string
  });

  const [preview, setPreview] = useState(null);

  // Cleanup preview URL on unmount
  useEffect(() => {
    return () => {
      if (preview && preview.startsWith("blob:")) {
        URL.revokeObjectURL(preview);
      }
    };
  }, [preview]);

  // Populate form when editing
  useEffect(() => {
    if (initialValues) {
      const { id, productId, type, imageUrl } = initialValues;

      setFormState({
        id: id || null,
        productId: productId || "",
        type: type || "",
        imageUrl: imageUrl || null,
      });

      // Set preview
      if (imageUrl) {
        if (typeof imageUrl === "string") {
          setPreview(`${apiUrl}/${imageUrl}`);
        } else if (imageUrl instanceof File) {
          setPreview(URL.createObjectURL(imageUrl));
        }
      } else {
        setPreview(null);
      }
    } else {
      resetForm();
    }
  }, [initialValues]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ["image/jpeg", "image/png", "image/webp", "image/gif"];
      if (!validTypes.includes(file.type)) {
        alert("Only JPG, PNG, WebP, and GIF images are allowed.");
        return;
      }

      setFormState((prev) => ({ ...prev, imageUrl: file }));
      setPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const formData = new FormData();

    // Always include id if editing
    if (formState.id) {
      formData.append("id", formState.id);
    }

    formData.append("productId", formState.productId);
    formData.append("type", formState.type);

    // Append image: either new file or existing path
    if (formState.imageUrl instanceof File) {
      formData.append("imageUrl", formState.imageUrl);
    } else if (typeof formState.imageUrl === "string" && formState.imageUrl) {
      // Send existing image path (important for update without new upload)
      formData.append("imageUrl", formState.imageUrl);
    } else {
      alert("Image is required");
      return;
    }

    onSubmit(formData);
  };

  const resetForm = () => {
    setFormState({
      productId: "",
      type: "",
      imageUrl: null,
    });
    setPreview(null);
  };

  const handleClear = () => {
    resetForm();
  };

  return (
    <Form onSubmit={handleSubmit}>
      <Row className="mb-3">
        <Col md={4}>
          <Form.Group controlId="productId">
            <Form.Label>Product ID</Form.Label>
            <Form.Control
              type="number"
              name="productId"
              value={formState.productId}
              onChange={handleChange}
              placeholder="Enter product ID"
              required
            />
          </Form.Group>
        </Col>

        <Col md={4}>
          <Form.Group controlId="type">
            <Form.Label>Type</Form.Label>
            <Form.Control
              type="text"
              name="type"
              value={formState.type}
              onChange={handleChange}
              placeholder="e.g. thumbnail, gallery"
              required
            />
          </Form.Group>
        </Col>

        <Col md={4}>
          <Form.Group controlId="image">
            <Form.Label>Image</Form.Label>
            <Form.Control type="file" accept="image/*" onChange={handleFileChange} />
            {preview && (
              <div className="mt-2">
                <img
                  src={preview}
                  alt="Preview"
                  style={{
                    width: "100px",
                    height: "100px",
                    objectFit: "cover",
                    marginTop: "10px",
                    border: "1px solid #ddd",
                  }}
                />
              </div>
            )}
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col className="text-end">
          <Button variant="secondary" className="me-2" type="button" onClick={handleClear}>
            Clear
          </Button>
          <Button variant="primary" type="submit">
            {initialValues ? "Update" : "Submit"}
          </Button>
        </Col>
      </Row>
    </Form>
  );
};

export default ProductImageForm;